import React from 'react';
import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { Braces } from 'lucide-react';
import { cn } from '@cotera/client/v0/lib/utils';
import { pillLabelForRef, sourceLabelOfRef } from '../variable-picker-data';
import { useTokenSourceLabels } from './token-source-labels';

/**
 * Renders a variable pill: a mono, accent-tinted chip showing the dotted path of
 * the referenced field, or the source's name when the whole of its output is
 * referenced. The tooltip names the source either way, since the path alone does
 * not say which step it came from.
 */
export const VariablePillView: React.FC<NodeViewProps> = ({ node }) => {
  const ref = String(node.attrs['ref'] ?? '');
  const attrLabel = node.attrs['label'];
  const sourceLabels = useTokenSourceLabels();
  const savedLabel = typeof attrLabel === 'string' ? attrLabel : null;
  const label = pillLabelForRef({ ref, sourceLabels, savedLabel });
  const source = sourceLabelOfRef(ref, sourceLabels);
  return (
    <NodeViewWrapper
      as="span"
      className="inline-block max-w-full align-baseline"
    >
      <span
        title={
          source === null || source === label ? label : `${source} · ${label}`
        }
        contentEditable={false}
        className={cn(
          // `max-w-full` + the label's `overflow-wrap:anywhere` let a long dotted
          // ref wrap inside the narrow (380px) inspector rather than overflow and
          // get clipped — the whole name stays readable without widening the panel.
          'mx-px inline-flex max-w-full items-baseline gap-1 rounded-[5px] px-1.5 py-0.5 align-baseline',
          'bg-primary/10 text-primary font-mono text-[12.5px] leading-tight',
          'cursor-default select-none'
        )}
      >
        <Braces className="size-3 shrink-0 translate-y-px opacity-70" />
        <span className="min-w-0 [overflow-wrap:anywhere]">{label}</span>
      </span>
    </NodeViewWrapper>
  );
};
